import React from 'react';

/**
 * RoleTrackCircles - TAM/SAM/SOM visualization + probability math
 *
 * Shows concentric rings (1 per track) with "YOU" at center.
 * More rings = bigger addressable market.
 * Right panel shows per-track probability and combined hero metric.
 */

const RING_COLORS = ['#f7c948', '#3d85c6', '#6aa84f', '#cc4125', '#9900ff'];

function computeCombinedProbability(tracks) {
  if (!tracks || tracks.length === 0) return 0;
  let pNoOffer = 1;
  for (const track of tracks) {
    const n = track.estimatedJobCount || 10;
    const p = track.estimatedSuccessRate || 0.006;
    pNoOffer *= Math.pow(1 - p, n);
  }
  return Math.min(0.99, 1 - pNoOffer);
}

function getProbabilityColor(prob) {
  if (prob >= 0.8) return '#22c55e';
  if (prob >= 0.5) return '#f59e0b';
  return '#ef4444';
}

const RoleTrackCircles = ({ tracks, maxTracks, effectiveTier, compact = false }) => {
  const baseRadius = compact ? 20 : 30;
  const ringSpacing = compact ? 16 : 22;
  const totalRings = maxTracks;
  const svgSize = baseRadius * 2 + totalRings * ringSpacing * 2 + 20;
  const center = svgSize / 2;
  const combined = computeCombinedProbability(tracks);

  if (compact) {
    // Compact version: just the circles + combined probability
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
          {Array.from({ length: totalRings }).map((_, i) => {
            const r = baseRadius + (totalRings - 1 - i) * ringSpacing;
            const isActive = i < tracks.length;
            return (
              <circle
                key={i}
                cx={center}
                cy={center}
                r={r}
                fill="none"
                stroke={isActive ? RING_COLORS[i % RING_COLORS.length] : '#e5e5e5'}
                strokeWidth={isActive ? 2 : 1}
                strokeDasharray={isActive ? 'none' : '4 4'}
                opacity={isActive ? 0.7 : 0.3}
              />
            );
          })}
          <circle cx={center} cy={center} r={6} fill="#f7c948" />
          <text x={center} y={center + 2.5} textAnchor="middle" fontSize="5" fill="#3d3d3d" fontWeight="700">
            YOU
          </text>
        </svg>
        <div style={{ fontSize: '11px', color: '#7a7a7a' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: getProbabilityColor(combined) }}>
            {(combined * 100).toFixed(0)}%
          </div>
          <div>chance in 90 days</div>
        </div>
      </div>
    );
  }

  // Full version: two-panel layout
  return (
    <div style={{
      display: 'flex',
      gap: '24px',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: '16px 0',
    }}>
      {/* Left: SVG circles */}
      <div style={{ textAlign: 'center', flexShrink: 0 }}>
        <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
          {/* Background rings (all slots) */}
          {Array.from({ length: totalRings }).map((_, i) => {
            const r = baseRadius + (totalRings - 1 - i) * ringSpacing;
            const isActive = i < tracks.length;
            return (
              <g key={i}>
                <circle
                  cx={center}
                  cy={center}
                  r={r}
                  fill={isActive ? `${RING_COLORS[i % RING_COLORS.length]}08` : 'none'}
                  stroke={isActive ? RING_COLORS[i % RING_COLORS.length] : '#e5e5e5'}
                  strokeWidth={isActive ? 2.5 : 1}
                  strokeDasharray={isActive ? 'none' : '4 4'}
                  opacity={isActive ? 0.8 : 0.3}
                />
                {isActive && tracks[i] && (
                  <text
                    x={center}
                    y={center - r + 14}
                    textAnchor="middle"
                    fontSize="9"
                    fill={RING_COLORS[i % RING_COLORS.length]}
                    fontWeight="600"
                  >
                    {tracks[i].label || tracks[i].targetRole}
                  </text>
                )}
              </g>
            );
          })}
          {/* Center dot */}
          <circle cx={center} cy={center} r={8} fill="#f7c948" />
          <text x={center} y={center + 3} textAnchor="middle" fontSize="6" fill="#3d3d3d" fontWeight="700">
            YOU
          </text>
        </svg>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          {tracks.length} of {maxTracks} tracks active
        </div>
      </div>

      {/* Right: Probability math */}
      <div style={{ flex: 1, minWidth: '200px' }}>
        {tracks.map((track, i) => {
          const n = track.estimatedJobCount || 10;
          const p = track.estimatedSuccessRate || 0.006;
          const pTrack = 1 - Math.pow(1 - p, n);

          return (
            <div
              key={track.id}
              style={{
                padding: '8px 12px',
                marginBottom: '8px',
                borderLeft: `3px solid ${RING_COLORS[i % RING_COLORS.length]}`,
                background: '#f9fafb',
                borderRadius: '0 6px 6px 0',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '2px' }}>
                {track.label || track.targetRole}
              </div>
              <div style={{ fontSize: '11px', color: '#7a7a7a', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span>~{n} jobs/mo</span>
                <span>|</span>
                <span>{(p * 100).toFixed(1)}% per app</span>
                <span>|</span>
                <span style={{ fontWeight: '600', color: getProbabilityColor(pTrack) }}>
                  P(offer) = {(pTrack * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })}

        {/* Combined hero metric */}
        <div style={{
          padding: '12px 16px',
          background: getProbabilityColor(combined) + '10',
          border: `1px solid ${getProbabilityColor(combined)}40`,
          borderRadius: '8px',
          marginTop: '12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '11px', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Combined probability (90 days)
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: getProbabilityColor(combined) }}>
            {(combined * 100).toFixed(1)}%
          </div>
          {tracks.length < maxTracks && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
              Add more tracks to increase your odds
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleTrackCircles;
